import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { HeartPulse, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function AuthPage() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/compte";

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : user.role === "professionnel" ? "/intranet" : from, { replace: true });
  }, [user, navigate, from]);

  if (user) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-ciel" />
      </div>
    );
  }

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(loginForm.email, loginForm.password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Bienvenue, ${res.user.name} !`);
      navigate(res.user.role === "admin" ? "/admin" : res.user.role === "professionnel" ? "/intranet" : from, { replace: true });
    } else toast.error(res.error);
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register(regForm);
    setLoading(false);
    if (res.ok) {
      toast.success("Compte créé. Bienvenue sur Vitalis Digital Store !");
      navigate(from, { replace: true });
    } else toast.error(res.error);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/compte";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md flex-col justify-center px-6 py-14" data-testid="auth-page">
      <div className="mb-8 text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-white">
          <HeartPulse size={22} />
        </span>
        <h1 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy">Vitalis Digital Store</h1>
        <p className="mt-1 text-sm text-slate-500">Accédez à vos formations et outils digitaux</p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <Tabs defaultValue={new URLSearchParams(location.search).get("tab") === "register" ? "register" : "login"}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" data-testid="tab-login">Connexion</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register">Inscription</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={doLogin} className="mt-5 space-y-4" data-testid="login-form">
              <Input data-testid="login-email" type="email" required placeholder="Email" value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} />
              <Input data-testid="login-password" type="password" required placeholder="Mot de passe" value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
              <Button data-testid="login-submit" disabled={loading} className="w-full rounded-full bg-navy py-5 text-sm font-semibold text-white hover:bg-navy-light">
                {loading ? <Loader2 className="animate-spin" size={16} /> : "Se connecter"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={doRegister} className="mt-5 space-y-4" data-testid="register-form">
              <Input data-testid="register-name" required minLength={2} placeholder="Nom complet (ex : Dr Awa Diallo)" value={regForm.name}
                onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} />
              <Input data-testid="register-email" type="email" required placeholder="Email" value={regForm.email}
                onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} />
              <Input data-testid="register-password" type="password" required minLength={6} placeholder="Mot de passe (6 caractères min.)" value={regForm.password}
                onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} />
              <Button data-testid="register-submit" disabled={loading} className="w-full rounded-full bg-ciel py-5 text-sm font-semibold text-white hover:bg-sky-700">
                {loading ? <Loader2 className="animate-spin" size={16} /> : "Créer mon compte"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
          <span className="h-px flex-1 bg-slate-200" /> ou <span className="h-px flex-1 bg-slate-200" />
        </div>

        <Button data-testid="google-login" onClick={googleLogin} variant="outline" className="w-full rounded-full py-5 text-sm font-semibold">
          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52Z"/></svg>
          Continuer avec Google
        </Button>
      </div>

      <p className="mt-6 text-center text-xs text-slate-400">
        En continuant, vous acceptez nos conditions. <Link to="/" className="text-ciel hover:underline">Retour à l'accueil</Link>
      </p>
    </div>
  );
}
